// MJOS-147 — MyJarvis agent backend (pi-web-ui-free).
//
// This module owns the pi-agent-core Agent + its supporting plumbing:
//   - a global fetch interceptor that attaches a fresh WorkOS bearer to the
//     agent proxy routes (/cf-ai/* + /api/agent-mcp);
//   - the Kimi K2.6 model wired to the /cf-ai/v1 proxy;
//   - the MyJarvis MCP tool bridge (read/write files, query Neon, KB pages, etc.);
//   - the composed system prompt.
//
// The chat UI (assistant-ui) drives this Agent. Nothing here imports
// @earendil-works/pi-web-ui — the Lit half is gone.
import {
	Agent,
	type AgentMessage,
	type AgentTool,
	convertToLlm,
} from "@earendil-works/pi-agent-core";
import { getModel } from "@earendil-works/pi-ai";
import { createMcpAgentTools } from "./mcp-bridge";

export type GetAccessToken = () => Promise<string | undefined>;

// Sentinel bearer/key value: a non-empty placeholder that short-circuits pi-ai's
// Node env-var key lookup and the MCP client's auth header. The fetch
// interceptor swaps in a fresh WorkOS token before any request leaves the
// browser, so this value never actually reaches an upstream. Shared by
// getApiKey (below) and the mcp-bridge bearerToken (agent-runtime → loadMcpBridge).
export const AUTH_VIA_INTERCEPTOR = "via-fetch-interceptor";

interface CachedMcpBridge {
	tools: AgentTool[];
	instructions: string;
	resources: { uri: string; name?: string; description?: string }[];
}

let cachedMcpBridge: CachedMcpBridge | null = null;

// Install ONE global fetch interceptor that adds Authorization: Bearer <fresh
// WorkOS token> to any request hitting our agent proxies (/cf-ai/* and
// /api/agent-mcp). The proxies then strip this header and swap in their
// server-side bearer before forwarding upstream. This keeps the long-lived
// CF Workers AI + MyJarvis MCP secrets out of the client bundle.
//
// We patch the GLOBAL fetch (not a per-request fetch seam) because pi-ai gives
// no hook to inject a custom fetch on the model-call path without rewriting its
// streamFn — the proxy URL rewrite (getKimiModel's baseUrl) rides on that same
// global fetch. So the global patch is the minimal correct seam here; do NOT
// swap it for a streamFn rewrite.
//
// The token getter is read from a MUTABLE module-level ref that
// installFetchInterceptor refreshes on every call. The patch itself is applied
// once, but a remount with a new getAccessToken updates the ref — so a fresh
// closure's token getter is always the one used.
let interceptorInstalled = false;
let currentGetAccessToken: GetAccessToken = async () => undefined;
function installFetchInterceptor(getAccessToken: GetAccessToken) {
	currentGetAccessToken = getAccessToken;
	if (interceptorInstalled) return;
	interceptorInstalled = true;
	const originalFetch = globalThis.fetch.bind(globalThis);
	globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
		const urlString =
			input instanceof Request
				? input.url
				: input instanceof URL
					? input.toString()
					: String(input);
		const isAgentProxy =
			urlString.includes("/cf-ai/") || urlString.includes("/api/agent-mcp");
		if (!isAgentProxy) return originalFetch(input, init);
		const token = await currentGetAccessToken();
		if (!token) {
			console.warn("[agent-panel] No WorkOS access token — proxy call will fail.");
			return originalFetch(input, init);
		}
		const headers = new Headers(
			init?.headers || (input instanceof Request ? input.headers : undefined),
		);
		headers.set("Authorization", `Bearer ${token}`);
		if (input instanceof Request) {
			return originalFetch(new Request(input, { headers }), init);
		}
		return originalFetch(input, { ...init, headers });
	};
}

function getKimiModel() {
	const model = getModel("cloudflare-workers-ai", "@cf/moonshotai/kimi-k2.6");
	return { ...model, baseUrl: `${window.location.origin}/cf-ai/v1` };
}

async function loadMcpBridge(): Promise<CachedMcpBridge> {
	if (cachedMcpBridge) return cachedMcpBridge;
	try {
		const bridge = await createMcpAgentTools({
			url: `${window.location.origin}/api/agent-mcp`,
			// Auth happens via the fetch interceptor; this is just a non-empty
			// placeholder that gets overwritten before the request leaves the browser.
			bearerToken: AUTH_VIA_INTERCEPTOR,
			labelPrefix: "MyJarvis ",
		});
		cachedMcpBridge = {
			tools: bridge.tools,
			instructions: bridge.instructions,
			resources: bridge.resources,
		};
		console.log(
			`[agent-panel] MCP connected — ${bridge.tools.length} tools, ${bridge.resources.length} resources, instructions ${bridge.instructions.length} chars.`,
		);
		return cachedMcpBridge;
	} catch (err) {
		console.error("[agent-panel] Failed to load MCP bridge:", err);
		return { tools: [], instructions: "", resources: [] };
	}
}

function composeSystemPrompt(bridge: CachedMcpBridge, userEmail?: string): string {
	const youFor = userEmail ? `for ${userEmail}` : "for the dashboard owner";
	const preamble = `You are MyJarvis, a helpful AI assistant ${youFor}.

You have access to several tools:
- MyJarvis dashboard tools: read/write files in the dashboard repo, query the Neon database, create/update KB pages, send voice messages, manage Cloudflare Pages builds, and more.
- read_mcp_resource(uri): Fetch the contents of an MCP resource by URI. Use this for any URI that looks like \`skill://...\` or other non-repo schemes. Do NOT try \`read_file\` for those.

You are running on Kimi K2.6 via Cloudflare Workers AI. Be direct, concise, and helpful. When the user asks about their dashboard, projects, tickets, or KB content, use the MyJarvis tools to look things up before guessing.`;

	const parts = [preamble];
	if (bridge.instructions) {
		parts.push(
			`\n<mcp_server_instructions source="myjarvis-mcp">\n${bridge.instructions}\n</mcp_server_instructions>`,
		);
	}
	if (bridge.resources.length > 0) {
		const catalog = bridge.resources
			.map((r) => `- ${r.uri}${r.description ? ` — ${r.description}` : ""}`)
			.join("\n");
		parts.push(
			`\n<mcp_resources count="${bridge.resources.length}">\nAvailable resources (fetch any with \`read_mcp_resource\`):\n${catalog}\n</mcp_resources>`,
		);
	}
	return parts.join("\n");
}

export interface CreateMyJarvisAgentOptions {
	getAccessToken: GetAccessToken;
	userEmail?: string;
}

/**
 * Build a fully-wired MyJarvis Agent: installs the auth interceptor, connects
 * the MCP bridge, composes the system prompt, and constructs the pi-agent-core
 * Agent on Kimi K2.6. Uses pi-agent-core's default `convertToLlm`.
 */
// Defensive process shim. pi-ai + pi-agent-core originate from a Node CLI and
// can read the Node `process` global on the model-call path (process.versions
// for env detection, etc.); in the browser bare `process` is undefined. The
// getApiKey sentinel already short-circuits pi-ai's env-var key lookup (the main
// path that touched process.env), so this is now belt-and-suspenders against any
// other stray `process` read — kept so a future pi-ai change can't silently
// dead-panel the agent again.
function ensureProcessShim() {
	const g = globalThis as unknown as { process?: { env?: Record<string, string> } };
	if (typeof g.process === "undefined") g.process = { env: {} };
	else if (!g.process.env) g.process.env = {};
}

export async function createMyJarvisAgent(
	opts: CreateMyJarvisAgentOptions,
): Promise<Agent> {
	ensureProcessShim();
	installFetchInterceptor(opts.getAccessToken);
	const bridge = await loadMcpBridge();
	const agent = new Agent({
		initialState: {
			systemPrompt: composeSystemPrompt(bridge, opts.userEmail),
			model: getKimiModel(),
			thinkingLevel: "off",
			messages: [] as AgentMessage[],
			tools: bridge.tools,
		},
		convertToLlm,
		// pi-ai resolves the provider API key via getApiKey, which defaults to a
		// Node env-var lookup (CLOUDFLARE_API_KEY) that is always empty in the
		// browser → "No API key for provider". Real auth is handled by the fetch
		// interceptor (a fresh WorkOS bearer swapped in for /cf-ai/* requests), so
		// a non-empty sentinel is all pi-ai needs to proceed with the request.
		getApiKey: () => AUTH_VIA_INTERCEPTOR,
	});
	// Dev-only debug handle — never shipped to prod.
	if (import.meta.env.DEV) {
		(globalThis as { __myJarvisAgent?: Agent }).__myJarvisAgent = agent;
	}
	return agent;
}
